import React, { useRef, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { PopperProps, Button } from "@mui/material";
import { Trans } from "react-i18next";
import { usePronounceFeedback } from "../../queries/stt/pronounceFeedback";
import WordLetterAnalysis from "./LetterFeedback";
import type { pronounceFeedbackQuery$data } from "queries/stt/__generated__/pronounceFeedbackQuery.graphql";
import { SUBSCRIPTION } from "root/profile/ProfileRouter.config";
import { useNavigate } from "react-router-dom";
import { useSetLastImpulse } from "hooks/useSetLastImpulse";
import ArrowPopper from "../other/ArrowPopper";
import { faCrown, faRefresh } from "@duocards/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AudioPlayButton, { AudioButton } from "./AudioPlayButton";

type PronounceFeedbackType = NonNullable<pronounceFeedbackQuery$data["pronounceFeedback"]>;

const VARIATION_STYLES = {
  success: {
    bg: (theme) => theme.duo.color.lightGreen,
    color: (theme) => theme.duo.color.green
  },
  error: {
    bg: (theme) => theme.duo.color.lightRed,
    color: (theme) => theme.duo.color.red
  },
  info: {
    bg: (theme) => theme.duo.palette.blueVeryLight2,
    color: (theme) => theme.duo.color.blueMedium
  }
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const MessageWrapper = styled.div<{ $centered: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  justify-content: ${({ $centered }) => ($centered ? "center" : "flex-start")};
`;

const ScoreBadge = styled.div<{ $variation: string }>`
  position: relative;
  min-width: 50px;
  min-height: 50px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  margin-right: 5px;
`;

const ScoreSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 50px;
  height: 50px;
  transform: rotate(-90deg);
`;

const ScoreText = styled.span<{ $variation: string }>`
  position: relative;
  color: ${({ theme, $variation }) => VARIATION_STYLES[$variation]?.color(theme)};
  z-index: 1;
`;

const Message = styled.div<{ $centered: boolean }>`
  flex: 1;
  font-size: 12px;
  font-weight: 400;
  word-break: break-word;
  text-align: ${({ $centered }) => ($centered ? "center" : "left")};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const GetScoreButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  padding: 4px 15px;
  border-radius: 999px;
  border: none;
  background: ${({ theme }) => theme.duo.color.white};
  font-weight: 600;
  font-size: 12px;
  box-shadow: none;
  text-transform: uppercase;
  align-self: center;
  .MuiButton-startIcon {
    margin-right: 0;
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const AudioControlsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 10px;
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.duo.color.themeGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;

export type PronounceFeedbackProps = {
  open: boolean;
  anchorEl: PopperProps["anchorEl"];
  pronunciationAudio?: string;
  text: string;
  lang: string;
  onClose: () => void;
  onTryAgain: () => void;
  onFeedback?: (feedback: any) => void;
  onLoading?: (isLoading: boolean) => void;
};

const PronounceFeedback: React.FC<PronounceFeedbackProps> = ({
  open,
  anchorEl,
  pronunciationAudio,
  text,
  lang,
  onClose,
  onFeedback,
  onLoading,
  onTryAgain
}) => {
  const navigate = useNavigate();
  const setLastImpulse = useSetLastImpulse();
  const [feedback, setFeedback] = useState<PronounceFeedbackType | undefined>();
  const { feedback: fetchedFeedback, isLoading } = usePronounceFeedback(text, pronunciationAudio, lang, {
    skip: !pronunciationAudio
  });

  useEffect(() => {
    onLoading?.(isLoading);
  }, [isLoading, onLoading]);

  useEffect(() => {
    if (fetchedFeedback) {
      setFeedback(fetchedFeedback);
      onFeedback?.(fetchedFeedback);
    } else if (!pronunciationAudio) {
      setFeedback(undefined);
    }
  }, [fetchedFeedback, pronunciationAudio, onFeedback]);

  const variation = !feedback || isLoading ? "info" : (feedback.score || 0) > 50 ? "success" : "error";

  const theme = useTheme();
  const bgColor = VARIATION_STYLES[variation]?.bg(theme);
  const color = VARIATION_STYLES[variation]?.color(theme);

  const isFuzzy = feedback?.method === "FUZZY";
  const displayPremiumButton = isFuzzy;
  const centeredText = isFuzzy;

  // Ensure anchorEl is HTMLElement | null for ArrowPopper
  const anchorElement =
    anchorEl && typeof anchorEl !== "function" && (anchorEl as HTMLElement).nodeType === 1
      ? (anchorEl as HTMLElement)
      : null;

  if (!feedback) return null;

  const handleTryAgain = (e) => {
    e.stopPropagation();
    onTryAgain();
  };

  return (
    <ArrowPopper
      open={open && !!anchorElement}
      anchorEl={anchorElement}
      bgColor={bgColor}
      color={color}
      onClose={onClose}
    >
      <Wrapper>
        <MessageWrapper $centered={centeredText}>
          {feedback.method === "AI" && typeof feedback.score === "number" && feedback.score > 0 && (
            <AnimatedScoreCircle score={feedback.score} variation={variation} theme={theme} />
          )}
          <Message $centered={centeredText}>
            <>
              {feedback.method === "AI" && <WordLetterAnalysis letterAnalysis={feedback.letter_analysis || []} />}
              {feedback.method === "FUZZY" && <Title>{feedback.transcript}</Title>}
              {feedback.feedback || ""}
            </>
          </Message>
        </MessageWrapper>
        <AudioControlsWrapper>
          {pronunciationAudio && (
            <AudioPlayButton text="you" audioConfig={{ type: "base64", audio: pronunciationAudio }} />
          )}
          <AudioPlayButton text="original" audioConfig={{ type: "tts", text, lang }} />
          <AudioButton onClick={handleTryAgain} startIcon={<FontAwesomeIcon icon={faRefresh} />}>
            <Trans>again</Trans>
          </AudioButton>
        </AudioControlsWrapper>
        {(displayPremiumButton || feedback?.remainingTries !== null) && (
          <GetScoreButton
            variant="outlined"
            onClick={() => {
              setLastImpulse("pronounceFeature");
              navigate(SUBSCRIPTION.url());
              onClose();
            }}
            startIcon={<FontAwesomeIcon icon={faCrown} />}
          >
            <GradientText>
              {displayPremiumButton ? (
                <Trans>Get Pronunciation Score</Trans>
              ) : (
                <>
                  <Trans>Remaining free tries:</Trans> {feedback.remainingTries}
                </>
              )}
            </GradientText>
          </GetScoreButton>
        )}
      </Wrapper>
    </ArrowPopper>
  );
};

const clampScore = (score: number) => Math.max(0, Math.min(score, 100));

type AnimatedScoreCircleProps = {
  score: number;
  variation: string;
  theme: any;
};

const CIRCLE_SIZE = 50;
const CIRCLE_RADIUS = 23;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
const ANIMATION_DURATION = 500; // ms

const AnimatedScoreCircle: React.FC<AnimatedScoreCircleProps> = ({ score, variation, theme }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const animationRef = useRef<number | null>(null);

  // Animate score and circle fill on score change
  useEffect(() => {
    if (typeof score !== "number") {
      setAnimatedValue(0);
      return;
    }

    const start = performance.now();
    const from = 0;
    const to = clampScore(score);

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const current = from + (to - from) * progress;
      setAnimatedValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimatedValue(to);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [score]);

  const strokeDashoffset = CIRCLE_CIRCUMFERENCE * (1 - clampScore(animatedValue) / 100);

  return (
    <ScoreBadge $variation={variation}>
      <ScoreSvg viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}>
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={CIRCLE_RADIUS - 2}
          fill="white"
          stroke="white"
          strokeWidth="4"
        />
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={CIRCLE_RADIUS}
          fill="none"
          stroke={VARIATION_STYLES[variation]?.color(theme)}
          strokeWidth="4"
          strokeDasharray={CIRCLE_CIRCUMFERENCE}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.5s linear" }}
        />
      </ScoreSvg>
      <ScoreText $variation={variation}>{Math.round(animatedValue)}</ScoreText>
    </ScoreBadge>
  );
};

export default PronounceFeedback;

export { AnimatedScoreCircle };
